// 3 LIBS
import moment from 'moment';

// ENVIRONMENT
import { environment } from 'src/environments/environment';

// ANGULAR
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';

// ANGULAR MATERIAL
import { MatIconRegistry } from '@angular/material/icon';
import { DateAdapter } from '@angular/material/core';

// @ngx-translate/core
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

// RxJS 6
import { Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

// API
import {
  IWHEnumWithTranslationsDTO,
  IWHMetadataEndpointConfigModel,
  WHMetadataDataService,
  WHThemeConfigService,
  diagnostics$,
  WHLayoutNavItemDOM,
  WHDomEventService,
  WHLoginDataService,
  WHAssetsENUM,
  MGMT_v1_GATEWAY,
} from '@workheld/workheld-shared-lib';

// ENV CONFIG
import { localDiagnosticsNavItems } from 'src/environments/env.config';
import { FormReferenceService } from './app-services-helper/form-reference.service';
import { WHFileAsyncService } from './app-services-async/w-h-file-async.service';
import { GoogleMapsLoaderService } from './app-services/google-maps.service';
import { UIService } from './app-services/ui.service';
import { AiStore } from './app-modules-pages/workheld-ai/wh-ai.store';
import { WH_AI_ICON_SVG } from './app-modules-pages/workheld-ai/config/wh-ai-icons';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('mainDrawer') mainDrawer: any;
  aiStore = inject(AiStore);
  private favIcon: HTMLLinkElement;

  // MANAGE SUBSCRIPTIONS
  private subscription: Subscription = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: any,
    private renderer2: Renderer2,
    private translateService: TranslateService,
    private metadataDataService: WHMetadataDataService,
    private themeConfigService: WHThemeConfigService,
    private loginDataService: WHLoginDataService,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private router: Router,
    private dateAdapter: DateAdapter<any>,
    private domEventService: WHDomEventService,
    private formReferenceService: FormReferenceService,
    private fileService: WHFileAsyncService,
    private uiService: UIService,
    private googleMapsLoaderService: GoogleMapsLoaderService,
    // private whCustomErrorService: WHCustomMonitoringService,
  ) {
    effect(() => {
      if (this.aiStore.isAiActive()) {
        this.mainDrawer.open();
      } else if (!this.aiStore.isAiActive() && this.mainDrawer) {
        this.mainDrawer.close();
      }
    });

    if (environment.instrumentationKey) {
      // this.whCustomErrorService.startMonitoring();
    }
    iconRegistry.addSvgIcon(
      'activate',
      sanitizer.bypassSecurityTrustResourceUrl(WHAssetsENUM.ACTIVATE_ICON),
    );
    iconRegistry.addSvgIcon(
      'deactivate',
      sanitizer.bypassSecurityTrustResourceUrl(WHAssetsENUM.DEACTIVATE_ICON),
    );
    iconRegistry.addSvgIcon(
      'workheld-icon',
      sanitizer.bypassSecurityTrustResourceUrl(WHAssetsENUM.WORKHELD_ICON),
    );
    iconRegistry.addSvgIcon(
      'workheld-sense-icon',
      sanitizer.bypassSecurityTrustResourceUrl(
        WHAssetsENUM.WORKHELD_SENSE_ICON,
      ),
    );
    iconRegistry.addSvgIcon(
      'person-planned',
      sanitizer.bypassSecurityTrustResourceUrl(WHAssetsENUM.PERSON_PLANNED),
    );
    iconRegistry.addSvgIconLiteral(
      'wh-ai-icon',
      sanitizer.bypassSecurityTrustHtml(WH_AI_ICON_SVG),
    );
  }
  public throwTestError(): void {
    throw new Error('Sentry Test Error');
  }
  ngOnInit(): void {
    this.googleMapsLoaderService
      .load()
      .then(() => console.log('🚀 Google Maps loaded'))
      // Maps is optional (and routinely blocked by iOS content blockers), so
      // a failed load must not surface as an unhandled application error.
      // console.log, not .warn: .warn is stripped in production (main.ts)
      .catch((error) => console.log('Google Maps failed to load', error));

    // DARK MODE
    this.subscription.add(
      this.themeConfigService.dark$.subscribe((dark: boolean) => {
        dark
          ? this.renderer2.addClass(this.document.body, 'dark')
          : this.renderer2.removeClass(this.document.body, 'dark');
      }),
    );

    // hack for WHP-7864
    setTimeout(() => {
      const sidenavContent = document.querySelector('mat-sidenav-content');

      if (sidenavContent) {
        this.renderer2.removeClass(sidenavContent, 'mat-drawer-content');
      }
    }, 1000);

    // end of hack

    // SET LOCAL DIAGNOSTICS PAGES
    const diagnostics: WHLayoutNavItemDOM[] = diagnostics$.value;
    diagnostics$.next(diagnostics.concat(localDiagnosticsNavItems));

    // LANGUAGE CHANGES
    this.subscription.add(
      this.translateService.onDefaultLangChange
        .pipe(
          switchMap((langChangeEvent: LangChangeEvent) => {
            // Activate Bryntum locale (loaded dynamically)
            import('src/app/app-pages/w-h-team-planner-page/team-planner-configs/team-planner-locale.config')
              .then((m) => m.applyBryntumLocale(langChangeEvent.lang))
              .catch(() => {});

            const endpointConfigMetadata: IWHMetadataEndpointConfigModel = {
              apiUrl: environment.apiUrl + MGMT_v1_GATEWAY,
            };
            moment.locale(langChangeEvent.lang);
            this.dateAdapter.setLocale(langChangeEvent.lang);
            this.document.documentElement.lang = langChangeEvent.lang;
            const tenantToken: string =
              this.loginDataService.activeTenantDOM$.value.token;
            const lngKey: string = this.translateService.langs?.includes(
              langChangeEvent.lang + '_' + tenantToken,
            )
              ? langChangeEvent.lang + '_' + tenantToken
              : langChangeEvent.lang;
            this.translateService.use(lngKey);
            return this.metadataDataService.getFlowEnumTranslations(
              endpointConfigMetadata,
              {
                language: langChangeEvent.lang,
                type: undefined,
                tenantToken: tenantToken,
              },
            );
          }),
        )
        .subscribe((enumTranslations: IWHEnumWithTranslationsDTO[]) => {
          this.metadataDataService.setMetadataTranslation(enumTranslations);
        }),
    );

    if (this.translateService?.defaultLang) {
      this.dateAdapter.setLocale(this.translateService.defaultLang);
    } else {
      this.dateAdapter.setLocale('de');
    }

    // NAVIGATION LISTENER
    this.subscription.add(
      this.router.events.subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          this.formReferenceService.unsavedChanges = false;
          this.domEventService.lastNavigationId = event.id;
          this.domEventService.routeEventsMap.set(event.id, event);
          if (environment.instrumentationKey) {
            // this.whCustomErrorService.logPageView(event.urlAfterRedirects);
          }
          this.uiService.$navigationStackCount.update((prev) => prev + 1);

          this._setLeftOffPath(event);
        }
      }),
    );

    // SET FAVICON
    this.favIcon = document.querySelector('#appFavicon');
    const tenantFavicon =
      this.loginDataService.activeTenantDOM$?.value?.customization?.favicon;
    if (tenantFavicon) {
      this.subscription.add(
        this.fileService
          .downloadWHFileBlob(tenantFavicon)
          .pipe(
            tap((blob) => {
              this.favIcon.href = window.URL.createObjectURL(blob);
            }),
          )
          .subscribe(),
      );
    } else {
      this.favIcon.href = './assets/img/workheld-icon.svg';
    }
  }

  // used for users that does not have permission
  // to view any work objects
  // displays a message with a links to pages user
  // visited before
  private _setLeftOffPath(event) {
    const path = event?.url.split('/').filter((part) => part !== '');
    const localStorageItem = localStorage.getItem('leftOffPath');
    const currentPath = localStorageItem ? JSON.parse(localStorageItem) : [];

    if (path[0] && !currentPath.find((p) => p === path[0])) {
      if (currentPath.length > 2) {
        currentPath.shift();
      }
      currentPath.push(path[0] as string);
    }
    localStorage.setItem('leftOffPath', JSON.stringify(currentPath));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
